import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userAPI } from '../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';

const ACK_VERSION = '1.0';
const SUPPLY_ROLES = ['host', 'service_provider', 'landlord', 'driver', 'delivery_rider', 'restaurant_partner'];
const ACK_TEXT =
  'I confirm that I am responsible for my own listing, property and services, that the details I provide are accurate, and that I hold the insurance, licences and consents required for what I offer. I understand that Ariv is a marketplace that connects me with customers and is not responsible for my conduct, my property, or the services I provide.';

/*
  One-time blocking acknowledgement for supply-side users (new and existing).
  Mounts globally so it appears on the next app load after login until accepted.
  FAIL-SAFE: it only renders when we can POSITIVELY determine the acknowledgement
  is needed; any missing data, unexpected shape, or error leaves the user
  un-gated so no one is ever locked out by this component.
*/
export default function ResponsibilityAcknowledgementGate() {
  const { user, loading, refreshUser, getAuthToken } = useAuth();
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  let needsAck = false;
  try {
    needsAck =
      !loading &&
      !!user &&
      Array.isArray(user.roles) &&
      user.roles.some((r) => SUPPLY_ROLES.includes(r)) &&
      user.responsibilityAck?.version !== ACK_VERSION;
  } catch (e) {
    needsAck = false;
  }

  if (!needsAck) return null;

  const accept = async () => {
    setSubmitting(true);
    setError('');
    try {
      await userAPI.acceptResponsibility(ACK_VERSION, getAuthToken());
      await refreshUser();
    } catch (e) {
      setError('We could not save that just now. Please check your connection and try again.');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true}>
      <DialogContent
        className="[&>button]:hidden max-w-md"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>One quick confirmation</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm text-gray-700">
          <p className="leading-relaxed">{ACK_TEXT}</p>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-1"
            />
            <span>I have read and agree to the above.</span>
          </label>
          {error && <p className="text-red-600">{error}</p>}
          <div className="flex justify-end pt-1">
            <Button
              disabled={!checked || submitting}
              onClick={accept}
              className="bg-[#1F3A68] hover:bg-[#1F3A68]/90"
            >
              {submitting ? 'Saving…' : 'I agree and continue'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
